import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button/Button';
import { StepNavigation } from '../StepNavigation/StepNavigation';
import { PlusIcon, TrashIcon, EditIcon, SparklesIcon } from '../Icons/Icons';
import { useEvaluationStore, useIsLoading } from '../../store/evaluationStore';
import { ProcessingAnimation } from '../ui/ProcessingAnimation/ProcessingAnimation';

export const CriteriaExtraction: React.FC = () => {
  const { t } = useTranslation();
  const { products, criteria, extractCriteriaAsync, setCurrentStep, updateCriterion, removeCriterion, setCriteria, saveDataAsync } = useEvaluationStore();
  const [descriptions, setDescriptions] = React.useState<string[]>(['']);
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
  const [editingData, setEditingData] = React.useState({ title: '', description: '' });
  const [addingNew, setAddingNew] = React.useState(false);
  const isLoading = useIsLoading();

  // インライン編集開始
  const startInlineEdit = (index: number) => {
    const criterion = criteria[index];
    setEditingIndex(index);
    setEditingData({
      title: criterion.title,
      description: criterion.description[0] || ''
    });
  };

  // 新規追加開始
  const startAddNew = () => {
    setAddingNew(true);
    setEditingData({ title: '', description: '' });
  };

  // 編集キャンセル
  const cancelEdit = () => {
    setEditingIndex(null);
    setAddingNew(false);
    setEditingData({ title: '', description: '' });
  };

  // 編集保存
  const saveEdit = () => {
    if (!editingData.title.trim()) return;

    const criterionData = {
      title: editingData.title.trim(),
      description: editingData.description.trim() ? [editingData.description.trim()] : [t('criteriaExtraction.newCriterionDescription')]
    };

    if (addingNew) {
      setCriteria([...criteria, criterionData]);
      setAddingNew(false);
    } else if (editingIndex !== null) {
      updateCriterion(editingIndex, criterionData);
      setEditingIndex(null);
    }

    setEditingData({ title: '', description: '' });
  };


  const handleUpdateDescription = (index: number, value: string) => {
    const newDescriptions = [...descriptions];
    newDescriptions[index] = value;
    setDescriptions(newDescriptions);
  };

  const handleAddDescription = () => {
    setDescriptions([...descriptions, '']);
  };

  const handleRemoveDescription = (index: number) => {
    if (descriptions.length > 1) {
      setDescriptions(descriptions.filter((_, i) => i !== index));
    }
  };

  const handleExtract = async () => {
    const validDescriptions = descriptions.filter(desc => desc.trim() !== '');
    if (validDescriptions.length > 0) {
      const success = await extractCriteriaAsync(validDescriptions);
      if (success && criteria.length > 0) {
        setDescriptions(['']); // Reset descriptions
      }
    }
  };

  const canProceed = criteria.length > 0;

  const handleNext = async () => {
    if (canProceed) {
      await saveDataAsync();
      setCurrentStep(3);
    }
  };

  // クリックイベントの伝播を停止
  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="section">
      <StepNavigation 
        currentStep={2}
        canProceed={canProceed}
        onNext={handleNext}
        onPrev={() => setCurrentStep(1)}
      />
      <div className="section__content">
        <div className="section__title-wrapper">
          <h2 className="section__title section__title--centered">
            {t('criteriaExtraction.title')}
          </h2>
          <p className="section__description section__description--centered">
            {t('criteriaExtraction.description')}
          </p>
        </div>

        {isLoading ? (
          <ProcessingAnimation 
            title={t('criteriaExtraction.generation.extracting')}
            description={t('criteriaExtraction.generation.extractingDescription')}
          />
        ) : (
          <>
            {/* 簡易入力フォーム（観点がない場合のみ表示） */}
            {criteria.length === 0 && !addingNew && (
              <div className="criteria-quick-input">
                <div className="criteria-input-form__content">
                  <div className="criteria-input-form__header">
                    <h3 className="criteria-input-form__title">
                      {t('criteriaExtraction.inputFormTitle')}
                    </h3>
                    <p className="criteria-input-form__subtitle">
                      {t('criteriaExtraction.inputFormSubtitle')}
                    </p>
                  </div>
                  <div className="criteria-descriptions-section">
                    {descriptions.map((desc, index) => (
                      <div key={index} className="description-input-row">
                        <div className="description-input-wrapper">
                          {index === 0 && (
                            <label className="input-label">
                              {t('criteriaExtraction.productDescriptionsLabel')}
                            </label>
                          )}
                          <textarea
                            className="input textarea description-textarea"
                            placeholder={t('criteriaExtraction.productDescriptionPlaceholder')}
                            value={desc}
                            onChange={(e) => handleUpdateDescription(index, e.target.value)}
                            rows={3}
                          />
                        </div>
                        {descriptions.length > 1 && (
                          <button
                            type="button"
                            className="description-remove-button"
                            onClick={() => handleRemoveDescription(index)}
                            title={t('criteriaExtraction.removeDescription')}
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="criteria-form-actions">
                    <Button
                      variant="primary"
                      size="md"
                      leftIcon={<SparklesIcon />}
                      onClick={handleExtract}
                      disabled={!descriptions.some(desc => desc.trim() !== '')}
                      className="extract-criteria-button"
                    >
                      {t('criteriaExtraction.extractButton')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* 観点グリッド */}
            {(criteria.length > 0 || addingNew) && (
              <>
                {criteria.length > 0 && (
                  <h3 className="criteria-list__title">
                    {t('criteriaExtraction.criteriaListTitle', { count: criteria.length })}
                  </h3>
                )}
                
                <div className="criteria-grid">
                  {/* 新規作成カード */}
                  <div 
                    className="criterion-card criterion-card--add"
                    onClick={startAddNew}
                  >
                    {addingNew ? (
                      // 新規追加フォーム
                      <div className="criterion-card__edit-form" onClick={stopPropagation}>
                        <div className="criterion-card__edit-field">
                          <input
                            type="text"
                            className="criterion-card__edit-input"
                            placeholder={t('criteriaExtraction.criterionTitlePlaceholder')}
                            value={editingData.title}
                            onChange={(e) => setEditingData({ ...editingData, title: e.target.value })}
                            autoFocus
                          />
                        </div>
                        <div className="criterion-card__edit-field">
                          <textarea
                            className="criterion-card__edit-textarea"
                            placeholder={t('criteriaExtraction.criterionDescriptionPlaceholder')}
                            value={editingData.description}
                            onChange={(e) => setEditingData({ ...editingData, description: e.target.value })}
                            rows={3}
                          />
                        </div>
                        <div className="criterion-card__edit-actions">
                          <button
                            className="criterion-card__edit-btn criterion-card__edit-btn--save"
                            onClick={saveEdit}
                            disabled={!editingData.title.trim()}
                          >
                            {t('common.save')}
                          </button>
                          <button
                            className="criterion-card__edit-btn criterion-card__edit-btn--cancel"
                            onClick={cancelEdit}
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="criterion-card__add-content">
                        <span className="criterion-card__add-icon">
                          <PlusIcon />
                        </span>
                        <span className="criterion-card__add-text">{t('criteriaExtraction.addCriterionCard')}</span>
                      </div>
                    )}
                  </div>

                  {/* 既存の観点カード */}
                  {criteria.map((criterion, index) => (
                    <div 
                      key={index} 
                      className={`criterion-card ${editingIndex === index ? 'criterion-card--editing' : ''}`}
                      onClick={() => editingIndex !== index && startInlineEdit(index)}
                    >
                      {editingIndex === index ? (
                        // インライン編集フォーム
                        <div className="criterion-card__edit-form" onClick={stopPropagation}>
                          <div className="criterion-card__edit-field">
                            <input
                              type="text"
                              className="criterion-card__edit-input"
                              placeholder={t('criteriaExtraction.criterionTitlePlaceholder')}
                              value={editingData.title}
                              onChange={(e) => setEditingData({ ...editingData, title: e.target.value })}
                              autoFocus
                            />
                          </div>
                          <div className="criterion-card__edit-field">
                            <textarea
                              className="criterion-card__edit-textarea"
                              placeholder={t('criteriaExtraction.criterionDescriptionPlaceholder')}
                              value={editingData.description}
                              onChange={(e) => setEditingData({ ...editingData, description: e.target.value })}
                              rows={3}
                            />
                          </div>
                          <div className="criterion-card__edit-actions">
                            <button
                              className="criterion-card__edit-btn criterion-card__edit-btn--save"
                              onClick={saveEdit}
                              disabled={!editingData.title.trim()}
                            >
                              {t('common.save')}
                            </button>
                            <button
                              className="criterion-card__edit-btn criterion-card__edit-btn--cancel"
                              onClick={cancelEdit}
                            >
                              {t('common.cancel')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        // 通常表示
                        <div className="criterion-card__content">
                          <h4 className="criterion-card__title">
                            {criterion.title}
                          </h4>
                          <p className="criterion-card__description">
                            {criterion.description[0]?.substring(0, 60)}...
                          </p>
                          <div className="criterion-card__overlay">
                            <div className="criterion-card__actions">
                              <button
                                className="criterion-action-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startInlineEdit(index);
                                }}
                                title={t('criteriaExtraction.editCriterion')}
                                aria-label={t('criteriaExtraction.editCriterion')}
                              >
                                <EditIcon />
                              </button>
                              <button
                                className="criterion-action-button criterion-action-button--delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeCriterion(index);
                                }}
                                title={t('criteriaExtraction.deleteCriterion')}
                                aria-label={t('criteriaExtraction.deleteCriterion')}
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};